import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Video } from "lucide-react";
import { api, WS_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FlirtCoach } from "@/components/FlirtCoach";

const ICEBREAKERS = [
  "What's your idea of a perfect night? 🌙",
  "Tell me your wildest fantasy…",
  "Truth or dare? 😏",
  "What are you wearing right now?",
  "First drink's on me — what are we having?",
];

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [partner, setPartner] = useState(null);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/messages/${matchId}`).then((r) => setMessages(r.data));
    api.get("/matches").then((r) => {
      const m = r.data.find((x) => x.match_id === matchId);
      if (m) setPartner(m.user);
    });

    const ws = new WebSocket(`${WS_BASE}/chat/${matchId}?token=${localStorage.getItem("pe_token")}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "message") {
        setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
      }
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [matchId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text;
    setText("");
    await api.post("/messages", { match_id: matchId, text: body });
  };

  return (
    <div className="flex flex-col h-screen md:h-screen" style={{ background: "#0A0A0A" }}>
      <header className="flex items-center gap-3 px-4 py-4 border-b border-[#2A2A2A]">
        <button data-testid="back-btn" onClick={() => navigate("/matches")} className="text-[#A0A0A0] hover:text-white">
          <ArrowLeft size={22} />
        </button>
        <img src={partner?.photos?.[0] || "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3"}
          alt="" className="w-10 h-10 rounded-full object-cover border border-[#D4AF37]/40" />
        <div className="flex-1">
          <div className="font-heading text-xl text-white">{partner?.name || "Match"}</div>
          <div className="text-xs text-[#A0A0A0]">{partner?.location}</div>
        </div>
        <button data-testid="start-call-btn" onClick={() => navigate(`/call/${matchId}`)}
          className="w-11 h-11 rounded-full gold-gradient flex items-center justify-center text-black hover:brightness-110 transition-all">
          <Video size={20} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-[#A0A0A0] text-sm mt-10">The night is young. Break the ice.</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${mine ? "gold-gradient text-black rounded-br-sm" : "bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-bl-sm"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="border-t border-[#2A2A2A]">
        {messages.length === 0 && (
          <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1" data-testid="icebreakers">
            {ICEBREAKERS.map((ib, i) => (
              <button type="button" key={i} data-testid={`icebreaker-${i}`} onClick={() => setText(ib)}
                className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                {ib}
              </button>
            ))}
          </div>
        )}
        <FlirtCoach matchId={matchId} onPick={setText} />
        <div className="flex items-center gap-3 p-4">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type something tempting…" className="pe-input flex-1 rounded-full px-5 py-3" />
          <button data-testid="send-btn" type="submit" className="w-12 h-12 rounded-full gold-gradient flex items-center justify-center text-black">
            <Send size={20} />
          </button>
        </div>
      </form>
    </div>
  );
}
